// Auto-initialize all carousels on page load and after Swup transitions
function autoInitCarousels() {
  const carousels = document.querySelectorAll('.carousel-container:not([data-carousel-initialized])');
  carousels.forEach(function (container) {
    container.setAttribute('data-carousel-initialized', 'true');
    initCarousel(container);
  });
}

// Cleanup carousels before Swup transitions
function cleanupCarousels() {
  const carousels = document.querySelectorAll('.carousel-container[data-carousel-initialized]');
  carousels.forEach(function (container) {
    // Stop autoplay if cleanup function exists
    if (container._stopAutoplay) {
      container._stopAutoplay();
    }
    // Disconnect the visibility observer so we don't leak listeners
    // when the carousel's DOM is swapped out by Swup.
    if (container._stopVisObserver) {
      container._stopVisObserver();
    }
    // Remove initialized flag so it can be re-initialized
    container.removeAttribute('data-carousel-initialized');
  });
}

// Swup page transition events
document.addEventListener('swup:willReplaceContent', cleanupCarousels);
document.addEventListener('swup:contentReplaced', autoInitCarousels);

// Custom carousel initialization function
export function initCarousel(container) {
  const track = container.querySelector('.carousel-track');
  let slides = Array.from(container.querySelectorAll('.carousel-slide'));
  const prevButton = container.querySelector('[data-carousel-control="prev"]');
  const nextButton = container.querySelector('[data-carousel-control="next"]');
  const pagination = container.querySelector('.carousel-pagination');
  const dots = pagination ? Array.from(pagination.querySelectorAll('.carousel-pagination__dot')) : [];

  const direction = container.getAttribute('data-direction') || 'horizontal';
  const effect = container.getAttribute('data-effect') || 'slide';
  const freemode = container.getAttribute('data-freemode') === 'true';
  const looping = container.getAttribute('data-looping') === 'true';
  const autoplay = container.getAttribute('data-autoplay') === 'true';
  const duration = parseInt(container.getAttribute('data-duration')) || 2500;

  let currentIndex = 0;
  let pendingAdvance = null;
  // Tracks whether the carousel is currently on-screen. The
  // IntersectionObserver below flips this; startAutoplay() refuses to
  // start while invisible. This is what keeps the nav-drawer's news
  // carousel in sync — its autoplay only runs while the drawer is open,
  // and resyncs from a known state every time the user reopens it.
  let isVisible = true;

  // When autoplay is on, slides containing video wait for the video's 'ended'
  // event before advancing. That requires the video to play once and stop —
  // so we strip the `loop` attribute the template ships with. Videos in
  // non-autoplay carousels keep looping as before.
  if (autoplay) {
    container.querySelectorAll('video').forEach((v) => v.removeAttribute('loop'));
  }

  // Initialize fade effect
  if (effect === 'fade' && slides.length > 0) {
    slides[0].classList.add('is-active');
  }

  // Setup infinite loop by cloning slides
  let realSlideCount = slides.length;
  const slidesPerView = parseFloat(track.getAttribute('data-slides-per-view')) || 1;

  if (looping && effect !== 'fade' && slides.length > 1) {
    // For multi-slide views, clone enough slides to fill the view
    const cloneCount = Math.ceil(slidesPerView);

    // Clone first slides and append to end
    for (let i = 0; i < cloneCount; i++) {
      const clone = slides[i].cloneNode(true);
      clone.setAttribute('data-cloned', 'true');
      track.appendChild(clone);
    }

    // Clone last slides and prepend to beginning
    for (let i = slides.length - 1; i >= Math.max(0, slides.length - cloneCount); i--) {
      const clone = slides[i].cloneNode(true);
      clone.setAttribute('data-cloned', 'true');
      track.insertBefore(clone, track.firstChild);
    }

    // Update slides array to include clones
    slides = Array.from(container.querySelectorAll('.carousel-slide'));

    // Start at the first real slide (skip the cloned last slides)
    currentIndex = cloneCount;
    if (direction === 'vertical') {
      track.scrollTop = slides[currentIndex].offsetTop;
    } else {
      track.scrollLeft = slides[currentIndex].offsetLeft;
    }
  }

  // Mouse/Touch drag functionality
  let isDragging = false;
  let startPos = 0;
  let currentPos = 0;
  let scrollStart = 0;
  let dragThreshold = 50;

  function handleDragStart(e) {
    isDragging = true;

    if (e.type.includes('mouse')) {
      startPos = direction === 'vertical' ? e.pageY : e.pageX;
      currentPos = startPos;
      if (effect !== 'fade') {
        scrollStart = direction === 'vertical' ? track.scrollTop : track.scrollLeft;
      }
    } else {
      startPos = direction === 'vertical' ? e.touches[0].pageY : e.touches[0].pageX;
      currentPos = startPos;
    }

    track.style.cursor = 'grabbing';
    track.style.userSelect = 'none';

    // Prevent default for mouse only to avoid ghost image
    if (e.type.includes('mouse')) {
      e.preventDefault();
    }
  }

  function handleDragMove(e) {
    if (!isDragging) return;

    if (e.type.includes('mouse')) {
      currentPos = direction === 'vertical' ? e.pageY : e.pageX;
    } else {
      currentPos = direction === 'vertical' ? e.touches[0].pageY : e.touches[0].pageX;
    }

    // For slide effect, update scroll position
    if (effect !== 'fade') {
      e.preventDefault();
      const walk = (currentPos - startPos) * 2;
      if (direction === 'vertical') {
        track.scrollTop = scrollStart - walk;
      } else {
        track.scrollLeft = scrollStart - walk;
      }
    }
  }

  function handleDragEnd() {
    if (!isDragging) return;

    isDragging = false;
    track.style.cursor = 'grab';
    track.style.userSelect = '';

    const dragDistance = currentPos - startPos;

    // For fade effect or when drag distance exceeds threshold, change slides
    if (effect === 'fade' || Math.abs(dragDistance) > dragThreshold) {
      if (Math.abs(dragDistance) > dragThreshold) {
        if (dragDistance > 0) {
          prevSlide();
        } else {
          nextSlide();
        }
        stopAutoplay();
      }
    }
  }

  // Mouse events
  track.addEventListener('mousedown', handleDragStart);
  document.addEventListener('mousemove', handleDragMove);
  document.addEventListener('mouseup', handleDragEnd);
  track.addEventListener('mouseleave', function () {
    if (isDragging) {
      handleDragEnd();
    }
  });

  // Touch events
  track.addEventListener('touchstart', handleDragStart, { passive: true });
  track.addEventListener('touchmove', handleDragMove, { passive: false });
  track.addEventListener('touchend', handleDragEnd);

  // Prevent click events when dragging
  track.addEventListener('click', function (e) {
    if (effect !== 'fade' && (Math.abs(track.scrollLeft - scrollStart) > 5 || Math.abs(track.scrollTop - scrollStart) > 5)) {
      e.preventDefault();
    }
  });

  // Update button states based on current position
  function updateButtons() {
    if (!prevButton || !nextButton) return;

    // With looping enabled, buttons are never disabled
    if (looping) {
      prevButton.disabled = false;
      nextButton.disabled = false;
    } else {
      prevButton.disabled = currentIndex === 0;
      nextButton.disabled = currentIndex === slides.length - 1;
    }
  }

  // Update pagination dots
  function updatePagination() {
    if (!pagination) return;

    // Calculate real index for looping
    let realIndex = currentIndex;
    if (looping && slides.length > realSlideCount) {
      const cloneCount = Math.ceil(slidesPerView);
      realIndex = ((currentIndex - cloneCount) % realSlideCount + realSlideCount) % realSlideCount;
    }

    dots.forEach(function (dot, index) {
      const isActive = index === realIndex;
      // Force animation restart by removing class first
      dot.classList.remove('is-active');
      if (isActive) {
        applyActiveDotDuration(dot);
        // Trigger reflow to restart CSS animation
        void dot.offsetWidth;
        dot.classList.add('is-active');
      }
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  // Set the dot's progress animation length to match how long the current
  // slide will actually stay on-screen: video length if the active slide
  // contains video, otherwise the configured autoplay duration. If video
  // metadata hasn't loaded yet we fall back to the configured duration and
  // update once 'loadedmetadata' fires.
  function applyActiveDotDuration(dot) {
    if (!autoplay) {
      dot.style.setProperty('--dot-progress-duration', duration + 'ms');
      return;
    }
    const slide = slides[currentIndex];
    const videos = slide ? Array.from(slide.querySelectorAll('video')) : [];
    const slideDuration = longestKnownVideoDuration(videos);

    if (slideDuration > 0) {
      dot.style.setProperty('--dot-progress-duration', slideDuration + 'ms');
    } else {
      dot.style.setProperty('--dot-progress-duration', duration + 'ms');
      // Refresh once any of the videos report their real length.
      videos.forEach((video) => {
        if (video.readyState >= 1) return;
        const onMeta = () => {
          video.removeEventListener('loadedmetadata', onMeta);
          if (!dot.classList.contains('is-active')) return;
          const updated = longestKnownVideoDuration(videos);
          if (updated <= 0) return;
          dot.classList.remove('is-active');
          dot.style.setProperty('--dot-progress-duration', updated + 'ms');
          void dot.offsetWidth;
          dot.classList.add('is-active');
        };
        video.addEventListener('loadedmetadata', onMeta);
      });
    }
  }

  function longestKnownVideoDuration(videos) {
    let ms = 0;
    videos.forEach((video) => {
      if (isFinite(video.duration) && video.duration > 0) {
        ms = Math.max(ms, video.duration * 1000);
      }
    });
    return ms;
  }

  // Scroll to specific slide
  function goToSlide(index, instant) {
    if (index < 0 || index >= slides.length) return;

    currentIndex = index;
    const slide = slides[index];

    if (effect === 'fade') {
      // Fade effect: toggle active class
      slides.forEach(function (s) {
        s.classList.remove('is-active');
      });
      slide.classList.add('is-active');
    } else {
      // Temporarily disable smooth scroll for instant jumps
      if (instant) {
        track.style.scrollBehavior = 'auto';
      } else if (freemode) {
        // For free mode, temporarily enable smooth scrolling for button/dot navigation
        track.style.scrollBehavior = 'smooth';
      }

      // Scroll effect: scroll to position
      if (direction === 'vertical') {
        track.scrollTop = slide.offsetTop;
      } else {
        track.scrollLeft = slide.offsetLeft;
      }

      // Restore scroll behavior
      if (instant || freemode) {
        setTimeout(function () {
          if (freemode) {
            track.style.scrollBehavior = 'auto';
          } else {
            track.style.scrollBehavior = 'smooth';
          }
        }, instant ? 0 : 500);
      }
    }

    updateButtons();
    updatePagination();

    // Announce to screen readers (use real index for looping)
    const realIndex = looping && slides.length > realSlideCount ? ((index - 1 + realSlideCount) % realSlideCount) : index;
    const announcement = 'Showing slide ' + (realIndex + 1) + ' of ' + realSlideCount;
    announceToScreenReader(announcement);
  }

  // Navigate to next slide
  function nextSlide() {
    const cloneCount = looping && slides.length > realSlideCount ? Math.ceil(slidesPerView) : 0;

    if (looping && slides.length > realSlideCount) {
      // Check if we're at the last real slide
      const lastRealSlideIndex = slides.length - cloneCount - 1;

      if (currentIndex === lastRealSlideIndex) {
        // Go to first clone
        goToSlide(currentIndex + 1);
        // After animation, jump to real first slide
        setTimeout(function () {
          goToSlide(cloneCount, true); // Instant jump
        }, 500);
      } else if (currentIndex < slides.length - 1) {
        goToSlide(currentIndex + 1);
      } else {
        // We're at a clone, jump back to beginning
        goToSlide(cloneCount, true);
      }
    } else if (currentIndex < slides.length - 1) {
      goToSlide(currentIndex + 1);
    } else if (looping) {
      // Fallback for fade effect
      goToSlide(0);
    }
  }

  // Navigate to previous slide
  function prevSlide() {
    const cloneCount = looping && slides.length > realSlideCount ? Math.ceil(slidesPerView) : 0;

    if (looping && slides.length > realSlideCount) {
      // Check if we're at the first real slide
      const firstRealSlideIndex = cloneCount;

      if (currentIndex === firstRealSlideIndex) {
        // Go to last clone
        goToSlide(currentIndex - 1);
        // After animation, jump to real last slide
        setTimeout(function () {
          goToSlide(slides.length - cloneCount - 1, true); // Instant jump
        }, 500);
      } else if (currentIndex > 0) {
        goToSlide(currentIndex - 1);
      } else {
        // We're at a clone, jump to end
        goToSlide(slides.length - cloneCount - 1, true);
      }
    } else if (currentIndex > 0) {
      goToSlide(currentIndex - 1);
    } else if (looping) {
      // Fallback for fade effect
      goToSlide(slides.length - 1);
    }
  }

  // Detect current slide based on scroll position
  function detectCurrentSlide() {
    // Skip for fade effect as we control it manually
    if (effect === 'fade') return;

    const scrollPos = direction === 'vertical' ? track.scrollTop : track.scrollLeft;

    // Find the closest slide to current scroll position
    let closestIndex = 0;
    let closestDistance = Infinity;

    slides.forEach(function (slide, index) {
      const slidePos = direction === 'vertical' ? slide.offsetTop : slide.offsetLeft;
      const distance = Math.abs(scrollPos - slidePos);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    if (closestIndex !== currentIndex && closestIndex >= 0 && closestIndex < slides.length) {
      currentIndex = closestIndex;
      updateButtons();
      updatePagination();
    }
  }

  // Announce to screen readers (for dynamic updates)
  function announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    announcement.style.position = 'absolute';
    announcement.style.left = '-10000px';
    announcement.style.width = '1px';
    announcement.style.height = '1px';
    announcement.style.overflow = 'hidden';

    container.appendChild(announcement);

    setTimeout(function () {
      container.removeChild(announcement);
    }, 1000);
  }

  // Schedule the next auto-advance based on the currently active slide.
  // Slides that contain <video> wait for every video's 'ended' event before
  // advancing; slides with only images fall back to the configured duration.
  // Respects `isVisible` — refuses to schedule when the carousel is off-screen
  // (e.g. inside a closed nav drawer or below the fold). The IntersectionObserver
  // below reschedules when the carousel becomes visible again.
  // `fresh` is true when we're entering a slide for the first time (init or
  // auto-advance) and false when we're resuming after a hover/off-screen
  // pause — the difference is whether we rewind the video to 0 or let it
  // continue from where it was.
  function startAutoplay(fresh) {
    if (!autoplay) return;
    if (!isVisible) return;
    cancelPendingAdvance();

    const slide = slides[currentIndex];
    const videos = slide ? Array.from(slide.querySelectorAll('video')) : [];

    if (videos.length === 0) {
      pendingAdvance = {
        timerId: setTimeout(function () {
          nextSlide();
          startAutoplay(true);
        }, duration),
      };
      return;
    }

    const remaining = new Set(videos);
    const listeners = [];

    videos.forEach(function (video) {
      const onEnded = function () {
        remaining.delete(video);
        if (remaining.size === 0) {
          nextSlide();
          startAutoplay(true);
        }
      };
      video.addEventListener('ended', onEnded);
      listeners.push([video, onEnded]);

      if (fresh) {
        try { video.currentTime = 0; } catch (_) { /* not seekable yet */ }
      }
      if (video.ended) {
        // Playback finished while we were paused — advance on next tick so
        // we don't recurse through nextSlide inside this loop.
        setTimeout(onEnded, 0);
        return;
      }
      if (video.paused || fresh) {
        const attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
      }
    });

    pendingAdvance = { listeners: listeners };
  }

  function cancelPendingAdvance() {
    if (!pendingAdvance) return;
    if (pendingAdvance.timerId) clearTimeout(pendingAdvance.timerId);
    if (pendingAdvance.listeners) {
      pendingAdvance.listeners.forEach(function (pair) {
        pair[0].removeEventListener('ended', pair[1]);
      });
    }
    pendingAdvance = null;
  }

  // Force-restart the active dot's CSS progress animation. Used when the
  // carousel becomes visible again, so the dot's progress bar starts from
  // zero in sync with the freshly-started autoplay interval (instead of
  // showing wherever the animation drifted to while off-screen).
  function resyncActiveDotAnimation() {
    if (!dots.length) return;
    const cloneCount = looping && slides.length > realSlideCount ? Math.ceil(slidesPerView) : 0;
    let realIndex = currentIndex;
    if (looping && slides.length > realSlideCount) {
      realIndex = ((currentIndex - cloneCount) % realSlideCount + realSlideCount) % realSlideCount;
    }
    dots.forEach(function (dot, i) {
      if (i === realIndex) {
        dot.classList.remove('is-active');
        // Force reflow so the next add restarts the animation from 0.
        void dot.offsetWidth;
        dot.classList.add('is-active');
      }
    });
  }

  // Stop autoplay
  function stopAutoplay() {
    cancelPendingAdvance();
  }

  // Event listeners for buttons
  if (prevButton) {
    prevButton.addEventListener('click', function () {
      prevSlide();
      stopAutoplay();
    });
  }

  if (nextButton) {
    nextButton.addEventListener('click', function () {
      nextSlide();
      stopAutoplay();
    });
  }

  // Pagination dots. Anchor dots (used in the nav drawer news reel) sync
  // the visible slide to the focused dot, so keyboard users can preview
  // slides while tabbing, and the click/Enter default action follows the
  // dot's href to the linked page. Button dots keep the classic behaviour
  // where a click advances the carousel in place.
  dots.forEach(function (dot, index) {
    const cloneCount = looping && slides.length > realSlideCount ? Math.ceil(slidesPerView) : 0;
    const slideIndex = looping && slides.length > realSlideCount ? index + cloneCount : index;

    if (dot.tagName === 'A') {
      dot.addEventListener('focus', function () {
        goToSlide(slideIndex);
        stopAutoplay();
      });
    } else {
      dot.addEventListener('click', function () {
        goToSlide(slideIndex);
        stopAutoplay();
      });
    }
  });

  // Keyboard navigation
  track.addEventListener('keydown', function (e) {
    const key = e.key;

    if (direction === 'horizontal') {
      if (key === 'ArrowLeft') {
        e.preventDefault();
        prevSlide();
        stopAutoplay();
      } else if (key === 'ArrowRight') {
        e.preventDefault();
        nextSlide();
        stopAutoplay();
      }
    } else {
      if (key === 'ArrowUp') {
        e.preventDefault();
        prevSlide();
        stopAutoplay();
      } else if (key === 'ArrowDown') {
        e.preventDefault();
        nextSlide();
        stopAutoplay();
      }
    }

    if (key === 'Home') {
      e.preventDefault();
      const cloneCount = looping && slides.length > realSlideCount ? Math.ceil(slidesPerView) : 0;
      goToSlide(looping && slides.length > realSlideCount ? cloneCount : 0);
      stopAutoplay();
    } else if (key === 'End') {
      e.preventDefault();
      const cloneCount = looping && slides.length > realSlideCount ? Math.ceil(slidesPerView) : 0;
      goToSlide(looping && slides.length > realSlideCount ? slides.length - cloneCount - 1 : slides.length - 1);
      stopAutoplay();
    }
  });

  // Click-to-advance: when the carousel ships without arrows or pagination,
  // treat a click anywhere on the container as "next slide". Skips real
  // drags (detected via the drag handlers' pointer positions) and any click
  // that lands on an interactive child.
  if (!prevButton && !nextButton && !pagination) {
    container.addEventListener('click', function (e) {
      if (e.target.closest('a, button')) return;
      if (Math.abs(currentPos - startPos) > 5) return;
      nextSlide();
      stopAutoplay();
    });
  }

  // Detect scroll position changes (only for slide effect, not freemode)
  if (effect !== 'fade' && !freemode) {
    let scrollTimeout;
    track.addEventListener('scroll', function () {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(detectCurrentSlide, 100);
    });
  }

  // Pause autoplay on hover/focus. Resuming passes fresh=false so we don't
  // rewind a video that's already partway through playback.
  if (autoplay) {
    container.addEventListener('mouseenter', stopAutoplay);
    container.addEventListener('focusin', stopAutoplay);
    container.addEventListener('mouseleave', function () { startAutoplay(false); });
    container.addEventListener('focusout', function (e) {
      if (!container.contains(e.relatedTarget)) {
        startAutoplay(false);
      }
    });
  }

  // Pause autoplay when the carousel is off-screen (e.g. nav drawer
  // closed, or scrolled past) and resync its animation each time it
  // returns. This is what keeps the nav drawer's news reel feeling
  // fresh — every time the user reopens the nav, the active dot's
  // progress bar restarts from zero and the autoplay interval begins
  // a clean cycle, instead of resuming wherever a throttled timer had
  // drifted to.
  if (autoplay) {
    const visObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        const wasVisible = isVisible;
        isVisible = entry.isIntersecting;
        if (isVisible && !wasVisible) {
          resyncActiveDotAnimation();
          startAutoplay(false);
        } else if (!isVisible && wasVisible) {
          stopAutoplay();
        }
      });
    }, { threshold: 0 });
    visObserver.observe(container);

    // Initial visibility check — if the carousel starts off-screen
    // (e.g. inside a closed nav drawer with display:none), don't
    // bother running autoplay until it comes into view.
    isVisible = container.offsetParent !== null && container.getClientRects().length > 0;

    container._stopVisObserver = function () { visObserver.disconnect(); };
  }

  // Initialize
  updateButtons();
  updatePagination();
  startAutoplay(true);

  // Store cleanup function
  container._stopAutoplay = stopAutoplay;
}
